import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";

const PROVIDERS = [
  { key: "postmark", label: "Postmark", hint: "Server API token from Postmark → Servers → API Tokens.", link: "https://account.postmarkapp.com/servers" },
  { key: "resend", label: "Resend", hint: "API key starting with re_ from Resend → API Keys. Sender domain must be verified.", link: "https://resend.com/api-keys" },
  { key: "sendgrid", label: "SendGrid", hint: "API key starting with SG. with Mail Send permission. Sender must be verified.", link: "https://app.sendgrid.com/settings/api_keys" },
  { key: "mailgun", label: "Mailgun", hint: "Domain sending key + your verified sending domain. Pick the matching region.", link: "https://app.mailgun.com/settings/api_security" },
  { key: "ses", label: "Amazon SES", hint: "IAM access key with ses:SendEmail. Identity and sandbox status are per-region.", link: "https://console.aws.amazon.com/ses" },
  { key: "gmail_oauth", label: "Gmail (Google account)", hint: "Sends as your connected Google account. Connect Google in the Google tab, then reconnect once to grant send access.", link: null },
  { key: "gmail_smtp", label: "Gmail (app password)", hint: "Needs 2-step verification, then a 16-character app password.", link: "https://myaccount.google.com/apppasswords" },
  { key: "smtp", label: "Custom SMTP", hint: "Any SMTP server — Zoho, Fastmail, Mailgun SMTP, your own mail host.", link: null },
];

const isMasked = (v) => typeof v === "string" && v.trim().length > 0 && /^[•*]+$/.test(v.trim());

const Field = ({ label, children, hint }) => (  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

export const EmailPanel = ({ settings, onSet, onSaveAll }) => {
  const [status, setStatus] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const f = settings || {};
  const provider = f.email_provider || "postmark";
  const meta = PROVIDERS.find((p) => p.key === provider) || PROVIDERS[0];

  const loadStatus = useCallback(() => {
    api.get("/email/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const saveAndCheck = async () => {
    await onSaveAll();
    loadStatus();
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await onSaveAll();
      const { data } = await api.post("/email/test", { to: testTo });
      toast.success(`Test email sent via ${data.provider}`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed");
    } finally { setSending(false); }
  };

  const secret = (key) => (
    <Input
      data-testid={`email-${key.replace(/_/g, "-")}`}
      type="password"
      placeholder={status?.secrets_set?.[key] ? "Saved — type to replace" : ""}
      value={isMasked(f[key]) ? "" : (f[key] || "")}
      onChange={(e) => onSet({ [key]: e.target.value })}
    />
  );

  return (
    <div className="space-y-6" data-testid="email-panel">
      <Card className="studio-card">
        <CardHeader>
          <CardTitle className="text-base">How Mahano OS sends email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 max-w-2xl">
          <p className="text-xs text-muted-foreground">
            Invoice reminders, campaigns and test sends all go through the provider you pick here.
            Keys are stored server-side and never shown again once saved.
          </p>

          <Field label="Provider">
            <Select value={provider} onValueChange={(v) => onSet({ email_provider: v })}>
              <SelectTrigger data-testid="email-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((p) => (
                  <SelectItem key={p.key} value={p.key} data-testid={`email-provider-${p.key}`}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="flex items-start gap-2 rounded-lg border border-border p-3">
            {status?.ready && status?.provider === provider
              ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-[hsl(var(--halo-teal))]" />
              : <AlertCircle className="w-4 h-4 mt-0.5 text-[hsl(var(--studio-danger))]" />}
            <div className="text-xs space-y-1" data-testid="email-status">
              <div className="font-medium">
                {status?.provider !== provider
                  ? `${meta.label} selected — hit Save & check to confirm it works`
                  : status?.ready
                    ? `${status.provider_label} is ready — sending as ${status.from_email}`
                    : `${status?.provider_label || meta.label} isn't configured yet`}
              </div>
              {status?.provider === provider && !status?.ready && status?.missing?.length > 0 && (
                <div className="text-muted-foreground">Still needed: {status.missing.join(", ")}</div>
              )}
              <div className="text-muted-foreground">{meta.hint}</div>
              {meta.link && (
                <a href={meta.link} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-[hsl(var(--studio-primary))]"
                   data-testid="email-provider-link">
                  Open {meta.label} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="From name">
              <Input data-testid="email-from-name" value={f.email_from_name || ""}
                     onChange={(e) => onSet({ email_from_name: e.target.value })} />
            </Field>
            <Field label="From email">
              <Input data-testid="email-from-email" value={f.email_from_email || ""}
                     onChange={(e) => onSet({ email_from_email: e.target.value })} />
            </Field>
            <Field label="Reply-to">
              <Input data-testid="email-reply-to" value={f.email_reply_to || ""}
                     onChange={(e) => onSet({ email_reply_to: e.target.value })} />
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card className="studio-card">
        <CardHeader><CardTitle className="text-base">{meta.label} credentials</CardTitle></CardHeader>
        <CardContent className="space-y-4 max-w-2xl">
          {provider === "postmark" && (
            <>
              <Field label="Server token">{secret("postmark_server_token")}</Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Transactional stream">
                  <Input data-testid="email-postmark-stream" value={f.postmark_transactional_stream || ""}
                         onChange={(e) => onSet({ postmark_transactional_stream: e.target.value })} />
                </Field>
                <Field label="Broadcast stream">
                  <Input value={f.postmark_broadcast_stream || ""}
                         onChange={(e) => onSet({ postmark_broadcast_stream: e.target.value })} />
                </Field>
              </div>
              <Field label="Webhook secret">{secret("postmark_webhook_secret")}</Field>
            </>
          )}

          {provider === "resend" && <Field label="API key" hint="Starts with re_">{secret("resend_api_key")}</Field>}

          {provider === "sendgrid" && <Field label="API key" hint="Starts with SG.">{secret("sendgrid_api_key")}</Field>}

          {provider === "mailgun" && (
            <>
              <Field label="Sending key">{secret("mailgun_api_key")}</Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sending domain" hint="e.g. mg.yourstudio.com">
                  <Input data-testid="email-mailgun-domain" value={f.mailgun_domain || ""}
                         onChange={(e) => onSet({ mailgun_domain: e.target.value })} />
                </Field>
                <Field label="Region">
                  <Select value={f.mailgun_region || "us"} onValueChange={(v) => onSet({ mailgun_region: v })}>
                    <SelectTrigger data-testid="email-mailgun-region"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us">US (api.mailgun.net)</SelectItem>
                      <SelectItem value="eu">EU (api.eu.mailgun.net)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </>
          )}

          {provider === "ses" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Access key ID">
                  <Input data-testid="email-ses-key-id" value={f.ses_access_key_id || ""}
                         onChange={(e) => onSet({ ses_access_key_id: e.target.value })} />
                </Field>
                <Field label="Region" hint="Identities and sandbox status are per-region">
                  <Input data-testid="email-ses-region" placeholder="us-east-1" value={f.ses_region || ""}
                         onChange={(e) => onSet({ ses_region: e.target.value })} />
                </Field>
              </div>
              <Field label="Secret access key">{secret("ses_secret_access_key")}</Field>
            </>
          )}

          {provider === "gmail_oauth" && (
            <p className="text-xs text-muted-foreground" data-testid="email-gmail-oauth-note">
              {f.google_connected
                ? `Connected as ${f.google_email || "your Google account"}. If sending fails with a permission error, reconnect Google once — Mahano now also asks for send access.`
                : "Connect your Google account in the Google tab first, then come back and send a test."}
            </p>
          )}

          {provider === "gmail_smtp" && (
            <>
              <Field label="Gmail address">
                <Input data-testid="email-gmail-address" value={f.gmail_address || ""}
                       onChange={(e) => onSet({ gmail_address: e.target.value })} />
              </Field>
              <Field label="App password" hint="16 characters, spaces are ignored">{secret("gmail_app_password")}</Field>
            </>
          )}

          {provider === "smtp" && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Host">
                  <Input data-testid="email-smtp-host" placeholder="smtp.yourhost.com" value={f.smtp_host || ""}
                         onChange={(e) => onSet({ smtp_host: e.target.value })} />
                </Field>
                <Field label="Port">
                  <Input data-testid="email-smtp-port" type="number" value={f.smtp_port || 587}
                         onChange={(e) => onSet({ smtp_port: parseInt(e.target.value) || 587 })} />
                </Field>
                <Field label="Security">
                  <Select value={f.smtp_security || "tls"} onValueChange={(v) => onSet({ smtp_security: v })}>
                    <SelectTrigger data-testid="email-smtp-security"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tls">STARTTLS (587)</SelectItem>
                      <SelectItem value="ssl">SSL/TLS (465)</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Username">
                <Input data-testid="email-smtp-username" value={f.smtp_username || ""}
                       onChange={(e) => onSet({ smtp_username: e.target.value })} />
              </Field>
              <Field label="Password">{secret("smtp_password")}</Field>
            </>
          )}

          <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-border">
            <div className="flex-1 min-w-[200px]">
              <Field label="Send a test email to">
                <Input data-testid="email-test-to" placeholder="you@yourstudio.com" value={testTo}
                       onChange={(e) => setTestTo(e.target.value)} />
              </Field>
            </div>
            <Button data-testid="email-send-test" disabled={sending || !testTo} onClick={sendTest}>
              <Send className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Save & send test"}
            </Button>
            <Button variant="outline" data-testid="email-save-check" onClick={saveAndCheck}>Save & check</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
